import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import {
  SUPERFICIE_CLARA,
  SUPERFICIE_OSCURA,
  ajustarParaSuperficie,
  textoLegibleSobre
} from '../utils/color-contrast';

/** Personalización de la pantalla de login de un cliente, ya validada y lista para pintarse. */
export interface LoginBrandingView {
  clave: string;
  logoUrl: string | null;
  backgroundUrl: string | null;
  /** Color principal en #RRGGBB. */
  color: string | null;
  /** Color de texto legible sobre `color`: blanco u oscuro según su luminosidad. */
  colorTexto: string;
  /** `color` ajustado para verse como trazo (borde de foco, iconos) sobre la tarjeta clara. */
  colorTrazoClaro: string;
  /** Lo mismo sobre la tarjeta oscura. */
  colorTrazoOscuro: string;
}

type EstadoBranding = 'inactivo' | 'cargando' | 'listo';

/**
 * Personalización del login por cliente: m31.ionet.mx/{clave}.
 *
 * Si la URL trae una clave, se consulta al backend (que a su vez la consulta al SIIC) y se aplican
 * logotipo, fondo y color. Si la clave no existe, el SIIC no responde o no hay clave en la URL, no
 * hay personalización y se ve el login de siempre.
 *
 * Mientras se consulta, `estado` es 'cargando' y el login muestra el loader en lugar de mostrar
 * primero la pantalla por defecto y cambiarla después.
 */
@Injectable({ providedIn: 'root' })
export class LoginBrandingService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private static readonly ClaveGuardadaKey = 'm31_login_clave';
  private static readonly LogoGuardadoKey = 'm31_login_logo';
  private static readonly ClaveRegex = /^[A-Za-z0-9_-]{1,40}$/;
  private static readonly ColorRegex = /^#[0-9A-Fa-f]{6}$/;

  /** Rutas propias de la aplicación: nunca son la clave de un cliente. */
  private static readonly RutasReservadas = new Set([
    'login', 'chat', 'auth', 'frontchannel-logout', 'backend', 'assets'
  ]);

  /**
   * Tiempo máximo que se espera la consulta al backend: pasado eso, se ve el login normal.
   * El backend ya se rinde con el SIIC a los 4 s; este margen cubre la red hasta ahí.
   */
  private static readonly EsperaConsultaMs = 5000;

  /**
   * El logotipo es pequeño y es la identidad del cliente: se espera a que cargue para no dejar una
   * placa vacía. Una URL mal escrita en el SIIC no debe bloquear el login más que esto.
   */
  private static readonly EsperaLogoMs = 2000;

  /**
   * El fondo NO bloquea. Si ya está listo en este tiempo (caché, conexión rápida), el login nace
   * con él; si no, el login aparece con el color y la imagen entra con un fundido cuando baja.
   */
  private static readonly EsperaFondoInicialMs = 1200;
  private static readonly EsperaFondoMaximaMs = 20000;

  /** Si el loader llegó a verse, se mantiene lo justo para no parecer un parpadeo. */
  private static readonly LoaderApareceMs = 120;
  private static readonly LoaderMinimoMs = 700;

  readonly estado = signal<EstadoBranding>('inactivo');
  readonly branding = signal<LoginBrandingView | null>(null);

  /**
   * Logotipo del cliente para el HOME. Sale de la misma consulta que personaliza el login, pero
   * vive aparte de `branding` porque tiene que sobrevivir a dos cosas: que el login desaparezca al
   * entrar, y que Microsoft recargue la aplicación entera al volver (por eso también se guarda).
   */
  readonly logoCliente = signal<string | null>(null);

  /** La imagen de fondo ya bajó y puede pintarse. Puede pasar a true después de mostrar el login. */
  readonly fondoListo = signal(false);
  readonly cargando = computed(() => this.estado() === 'cargando');

  constructor() {
    this.iniciarDesdeLaUrl();
  }

  /** Clave con la que se entró en esta pestaña, si la hubo. Sirve para volver tras cerrar sesión. */
  claveGuardada(): string | null {
    try {
      return sessionStorage.getItem(LoginBrandingService.ClaveGuardadaKey);
    } catch {
      return null;
    }
  }

  /** Vuelve a cargar la personalización de la clave guardada (al cerrar sesión). */
  async restaurarTrasCerrarSesion(): Promise<string | null> {
    const clave = this.claveGuardada();
    if (!clave) {
      this.branding.set(null);
      this.publicarLogo(null);
      this.estado.set('inactivo');
      return null;
    }

    if (this.branding()?.clave !== clave) {
      await this.cargar(clave);
    }

    return clave;
  }

  private iniciarDesdeLaUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Quien ya tiene sesión no ve el login, así que no se consulta nada; solo se recupera el logo
    // para el HOME. Es el caso de volver de Microsoft: la aplicación arranca de cero.
    if (this.auth.isLoggedIn()) {
      this.recuperarLogoGuardado();
      return;
    }

    const segmento = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
    const enRutaPropia = LoginBrandingService.RutasReservadas.has(segmento.toLowerCase());

    if (segmento === '' || segmento.toLowerCase() === 'login') {
      // Entrar por la raíz es elegir el acceso general. La única excepción es volver de Microsoft,
      // que también aterriza en /login y debe conservar la clave con la que se empezó.
      if (!this.volviendoDeMicrosoft()) {
        this.olvidarClave();
      }
      return;
    }

    if (enRutaPropia || !LoginBrandingService.ClaveRegex.test(segmento)) {
      return;
    }

    this.guardarClave(segmento);
    void this.cargar(segmento);
  }

  private async cargar(clave: string): Promise<void> {
    const inicio = performance.now();
    this.estado.set('cargando');

    let resultado: LoginBrandingView | null = null;

    try {
      const respuesta = await firstValueFrom(
        this.http
          .get<Record<string, unknown>>(`${environment.apiUrl}/branding/${encodeURIComponent(clave)}`)
          .pipe(timeout(LoginBrandingService.EsperaConsultaMs))
      );

      if (respuesta && (respuesta['Found'] ?? respuesta['found']) === true) {
        resultado = await this.prepararVista(clave, respuesta);
      }
    } catch (error) {
      // Cualquier fallo significa lo mismo para el visitante: el login de siempre.
      console.warn('[M31] No se pudo cargar la personalización del login; se usa la predeterminada.', error);
    }

    // Si el loader alcanzó a mostrarse, no se quita de golpe: eso se ve como un parpadeo.
    const transcurrido = performance.now() - inicio;
    if (resultado
      && transcurrido > LoginBrandingService.LoaderApareceMs
      && transcurrido < LoginBrandingService.LoaderMinimoMs) {
      await new Promise(resolver => setTimeout(resolver, LoginBrandingService.LoaderMinimoMs - transcurrido));
    }

    this.branding.set(resultado);
    this.publicarLogo(resultado?.logoUrl ?? null);
    this.estado.set('listo');
  }

  private async prepararVista(clave: string, datos: Record<string, unknown>): Promise<LoginBrandingView | null> {
    const leer = (nombre: string): unknown => datos[nombre] ?? datos[nombre.charAt(0).toLowerCase() + nombre.slice(1)];

    // El backend ya validó esto; se vuelve a comprobar porque lo que sigue acaba dentro del CSS.
    const color = LoginBrandingService.colorSeguro(leer('PrimaryColor'));
    const logo = LoginBrandingService.urlSegura(leer('LogoUrl'));
    const fondo = LoginBrandingService.urlSegura(leer('BackgroundUrl'));

    // Las dos descargas arrancan a la vez: el peor caso (un servidor de imágenes que no responde)
    // cuesta lo que tarda el logotipo, no la suma de ambas.
    this.fondoListo.set(false);
    const descargaFondo = fondo
      ? this.precargar(fondo, LoginBrandingService.EsperaFondoMaximaMs)
      : Promise.resolve(false);

    const [logoOk, fondoAlArrancar] = await Promise.all([
      // El logotipo se descarta si no existe, en vez de dejar un icono roto.
      this.precargar(logo, LoginBrandingService.EsperaLogoMs),
      // El fondo no bloquea: si no está en este tiempo, el login sale sin él y la imagen entra después.
      Promise.race([
        descargaFondo,
        new Promise<null>(resolver => setTimeout(() => resolver(null), LoginBrandingService.EsperaFondoInicialMs))
      ])
    ]);

    if (fondoAlArrancar === true) {
      this.fondoListo.set(true);
    } else if (fondoAlArrancar === null) {
      void descargaFondo.then(ok => {
        // Solo si el visitante sigue en esta misma entrada.
        if (ok && this.branding()?.clave === clave) {
          this.fondoListo.set(true);
        }
      });
    }

    const vista: LoginBrandingView = {
      clave,
      logoUrl: logoOk ? logo : null,
      backgroundUrl: fondo,
      color,
      colorTexto: color ? textoLegibleSobre(color) : '#FFFFFF',
      colorTrazoClaro: color ? ajustarParaSuperficie(color, SUPERFICIE_CLARA) : '#FFFFFF',
      colorTrazoOscuro: color ? ajustarParaSuperficie(color, SUPERFICIE_OSCURA) : '#FFFFFF'
    };

    // Si no queda nada que aplicar, es lo mismo que no tener personalización.
    return vista.logoUrl || vista.backgroundUrl || vista.color ? vista : null;
  }

  private precargar(url: string | null, limiteMs: number): Promise<boolean> {
    if (!url) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>(resolver => {
      const imagen = new Image();
      const limite = setTimeout(() => resolver(false), limiteMs);

      imagen.referrerPolicy = 'no-referrer';
      imagen.onload = () => { clearTimeout(limite); resolver(true); };
      imagen.onerror = () => { clearTimeout(limite); resolver(false); };
      imagen.src = url;
    });
  }

  private volviendoDeMicrosoft(): boolean {
    const fuente = `${window.location.search}&${window.location.hash}`.toLowerCase();
    return fuente.includes('code=') && fuente.includes('state=');
  }

  private guardarClave(clave: string): void {
    try {
      sessionStorage.setItem(LoginBrandingService.ClaveGuardadaKey, clave);
    } catch { /* sin almacenamiento: solo se pierde volver a la ruta tras cerrar sesión */ }
  }

  private olvidarClave(): void {
    try {
      sessionStorage.removeItem(LoginBrandingService.ClaveGuardadaKey);
    } catch { /* idem */ }

    // El logo se va con la clave: quien entra por la raíz no debe heredar el de otra entrada.
    this.publicarLogo(null);
  }

  /** Deja el logo disponible para el HOME y guardado por si la aplicación se recarga. */
  private publicarLogo(url: string | null): void {
    this.logoCliente.set(url);

    try {
      if (url) {
        sessionStorage.setItem(LoginBrandingService.LogoGuardadoKey, url);
      } else {
        sessionStorage.removeItem(LoginBrandingService.LogoGuardadoKey);
      }
    } catch { /* sin almacenamiento: el logo solo no sobrevive a una recarga */ }
  }

  private recuperarLogoGuardado(): void {
    try {
      // Se vuelve a validar: es lo que acabará en un <img>, venga de donde venga.
      this.logoCliente.set(LoginBrandingService.urlSegura(sessionStorage.getItem(LoginBrandingService.LogoGuardadoKey)));
    } catch {
      this.logoCliente.set(null);
    }
  }

  /** Solo URL https sin caracteres que puedan salirse de un url("...") de CSS. */
  static urlSegura(valor: unknown): string | null {
    if (typeof valor !== 'string' || valor.length === 0 || valor.length > 500 || /["'()\\<>\s]/.test(valor)) {
      return null;
    }

    try {
      const url = new URL(valor);
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  static colorSeguro(valor: unknown): string | null {
    return typeof valor === 'string' && LoginBrandingService.ColorRegex.test(valor) ? valor.toUpperCase() : null;
  }

}
